import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Mail, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { V2 } from '../v2/shared';

const ConfirmEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(!!token);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const response = await fetch('/api/auth/confirm-email', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }),
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok) setConfirmed(true);
        else setError(data.error || 'No pudimos confirmar tu email.');
      } catch {
        setError('Error de conexión. Probá de nuevo.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const resend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error('Revisá el formato del email'); return; }
    setResending(true);
    try {
      const response = await fetch('/api/auth/resend-confirmation', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) setResent(true);
      else toast.error(data.error || 'No pudimos reenviar el email.');
    } catch {
      toast.error('Error de conexión. Probá de nuevo.');
    } finally {
      setResending(false);
    }
  };

  const Icon = ({ children, tone }: { children: React.ReactNode; tone: string }) => (
    <div style={{ width: 72, height: 72, borderRadius: '50%', display: 'grid', placeItems: 'center', margin: '0 auto 18px', background: tone }}>{children}</div>
  );

  return (
    <V2>
      <main className="auth">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="panel">
            {loading && (<>
              <Icon tone="var(--p-100)"><Loader2 size={32} className="animate-spin" color="#553866" /></Icon>
              <h1 style={{ fontSize: 26 }}>Confirmando tu email…</h1>
              <p className="hint" style={{ marginTop: 8 }}>Un momento, por favor.</p>
            </>)}

            {!loading && confirmed && (<>
              <Icon tone="#e6f4ec"><CheckCircle2 size={36} color="#1f7a4d" /></Icon>
              <h1 style={{ fontSize: 26 }}>¡Email confirmado!</h1>
              <p className="hint" style={{ marginTop: 8 }}>Tu cuenta está activa. Ya podés ingresar.</p>
              <Link to="/login" className="btn2 btn2-cta" style={{ marginTop: 16, width: '100%' }}>Ingresar</Link>
            </>)}

            {!loading && !confirmed && (<>
              {token && error ? (<>
                <Icon tone="#fdecea"><XCircle size={36} color="#b42318" /></Icon>
                <h1 style={{ fontSize: 26 }}>No pudimos confirmar tu email</h1>
                <p className="hint" style={{ marginTop: 8 }}>{error} El enlace puede haber vencido (dura 30 minutos).</p>
              </>) : (<>
                <Icon tone="var(--p-100)"><Mail size={32} color="#553866" /></Icon>
                <h1 style={{ fontSize: 26 }}>Confirmá tu email</h1>
                <p className="hint" style={{ marginTop: 8 }}>Te enviamos un enlace de confirmación. Si no lo encontrás, pedí otro.</p>
              </>)}

              {resent ? (
                <div className="alert ok" role="status" style={{ marginTop: 18, textAlign: 'left' }}>
                  Si el email existe y falta confirmarlo, te enviamos un nuevo enlace. Revisá también spam.
                </div>
              ) : (
                <form onSubmit={resend} style={{ marginTop: 20, textAlign: 'left' }}>
                  <div className="field">
                    <label htmlFor="resend-email">Tu email</label>
                    <input id="resend-email" type="email" autoComplete="email" className="in" value={email} onChange={e => setEmail(e.target.value)} />
                  </div>
                  <button type="submit" className="btn2 btn2-solid" style={{ width: '100%' }} disabled={resending}>
                    {resending ? 'Enviando…' : 'Reenviar enlace de confirmación'}
                  </button>
                </form>
              )}
              <p className="auth-alt"><Link to="/login">Volver a ingresar</Link></p>
            </>)}
          </div>
        </div>
      </main>
    </V2>
  );
};

export default ConfirmEmail;
